import React, { useState, useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Search, Sparkles, Clock, AlertCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import SiteLayout from "@/components/site/SiteLayout";
import StructuredData from "@/components/site/StructuredData";
import { fetchBlogList, fetchBlogCategories } from "@/lib/cms";
import { getCmsApiBaseUrl } from "@/lib/cmsBase";

const SITE_BASE = typeof window !== "undefined" ? window.location.origin : "";

function fmt(d) {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (e) {
    return d;
  }
}

function toSlug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function BlogPage() {
  const { cat: catParam } = useParams();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derived: resolved category. Doesn't need useEffect/setState.
  const cat = useMemo(() => {
    if (!catParam) return "All";
    if (cats.length === 0) return "__pending__"; // not yet resolved
    const resolved = cats.find((c) => toSlug(c) === catParam);
    return resolved || "All";
  }, [catParam, cats]);

  // Load categories once
  useEffect(() => {
    let mounted = true;
    fetchBlogCategories()
      .then((c) => { if (mounted) setCats(c); })
      .catch(() => { /* swallow */ });
    return () => { mounted = false; };
  }, []);

  // Fetch posts whenever the resolved category changes
  useEffect(() => {
    if (cat === "__pending__") return undefined;
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { items: it } = await fetchBlogList({ category: cat });
        if (mounted) setItems(it);
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load articles");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [cat]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((p) =>
      `${p.title} ${p.excerpt} ${(p.tags || []).join(" ")}`.toLowerCase().includes(ql)
    );
  }, [q, items]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  // JSON-LD: ItemList for the listing
  const ld = useMemo(() => {
    if (!items.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: cat === "All" ? "CoSec Global Blog" : `${cat}. CoSec Global Blog`,
      itemListElement: items.slice(0, 20).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_BASE}/blog/${p.slug}`,
        name: p.title,
      })),
    };
  }, [items, cat]);

  return (
    <SiteLayout>
      <StructuredData data={ld} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-20 pb-10 max-w-4xl">
          <span className="pill">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> Insight from the CoSec Global team
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            Compliance, governance &amp;{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">UK Cosec</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>{" "}
            insights.
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            Practitioner-grade writing on UK company law, Companies House reform, the ECCT Act 2023, and the AI changing how Cosec teams work.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <a
              href={`${getCmsApiBaseUrl()}/blog-posts/rss.xml`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-primary hover:text-accent inline-flex items-center gap-1.5"
              data-testid="rss-link"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Subscribe via RSS
            </a>
            {catParam && (
              <Link to="/blog" className="text-xs font-bold text-muted-foreground hover:text-primary">Clear category filter</Link>
            )}
          </div>
        </div>
      </section>

      <section className="pb-6 border-b border-border">
        <div className="container-cv flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <CategoryPill label="All" to="/blog" active={cat === "All"} testid="blog-cat-all" />
            {cats.map((c) => (
              <CategoryPill
                key={c}
                label={c}
                to={`/blog/category/${toSlug(c)}`}
                active={cat === c}
                testid={`blog-cat-${toSlug(c)}`}
              />
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              data-testid="blog-search"
              placeholder="Search articles…"
              className="pl-9 h-11"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
      </section>

      {loading && (
        <div className="container-cv py-16 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading articles…
        </div>
      )}

      {error && !loading && (
        <div className="container-cv py-16">
          <div className="max-w-md mx-auto rounded-xl border border-destructive/30 bg-destructive/10 p-5 text-destructive text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
          </div>
        </div>
      )}

      {!loading && !error && featured && (
        <section className="py-12 md:py-16">
          <div className="container-cv">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group grid lg:grid-cols-12 gap-8 rounded-3xl border border-border bg-white overflow-hidden hover:shadow-[0_24px_60px_-24px_rgba(14,58,95,0.25)] transition-all"
            >
              <div className="lg:col-span-7 relative aspect-[16/9] lg:aspect-auto overflow-hidden">
                <img src={featured.cover} alt={featured.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="lg:col-span-5 p-6 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-accent/15 text-primary font-bold uppercase tracking-wider">{featured.category}</span>
                  <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {featured.readMins} min read</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">{fmt(featured.publishedAt)}</span>
                </div>
                <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight leading-[1.15] group-hover:text-accent transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-3 text-base text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                <div className="mt-5 flex items-center gap-2 text-sm font-bold text-primary group-hover:text-accent transition-colors">
                  Read article <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {!loading && !error && (
        <section className="pb-20 md:pb-28">
          <div className="container-cv">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-muted-foreground">No articles match your filters.</div>
                <button
                  data-testid="blog-reset"
                  onClick={() => { setQ(""); }}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent"
                >
                  Clear search
                </button>
              </div>
            ) : rest.length === 0 ? null : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group rounded-2xl border border-border bg-white overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.cover} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-[11px]">
                        <span className="px-2 py-0.5 rounded-full bg-accent/15 text-primary font-bold uppercase tracking-wider">{p.category}</span>
                        <span className="text-muted-foreground">{fmt(p.publishedAt)}</span>
                      </div>
                      <h3 className="mt-3 font-display text-lg font-bold text-primary leading-snug group-hover:text-accent transition-colors">{p.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-3 leading-relaxed">{p.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}

function CategoryPill({ label, to, active, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "bg-white border border-border text-primary/80 hover:border-accent/60 hover:text-primary"
      }`}
    >
      {label}
    </Link>
  );
}
